'use client'

import { useState, useRef, useEffect } from 'react'
import { Volume2, VolumeX } from 'lucide-react'

interface LoginVideoProps {
  url: string
  className?: string
}

function getYouTubeEmbedUrl(url: string): string | null {
  try {
    // Suporta formatos: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // ID direto
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) {
        // Inicia mudo (mute=1) para garantir autoplay e habilita API JS (enablejsapi=1)
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&loop=1&playlist=${match[1]}&controls=0&modestbranding=1&rel=0&enablejsapi=1&origin=${typeof window !== 'undefined' ? window.location.origin : ''}`
      }
    }
    return null
  } catch {
    return null
  }
}

function getBunnyCDNEmbedUrl(url: string): string | null {
  try {
    // Suporta formatos: iframe.mediadelivery.net/embed/LIBRARY_ID/VIDEO_ID
    const pattern = /(?:https?:\/\/)?iframe\.mediadelivery\.net\/embed\/(\d+)\/([a-zA-Z0-9-]+)/
    const match = url.match(pattern)

    if (match) {
      const [, libraryId, videoId] = match
      // Autoplay mudo para garantir reprodução automática
      return `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}?autoplay=true&muted=true&loop=true&preload=true`
    }
    return null
  } catch {
    return null
  }
}

export function LoginVideo({ url, className = '' }: LoginVideoProps) {
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(true) // Inicia mudo para garantir autoplay
  const videoRef = useRef<HTMLVideoElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  if (!url) return null

  const youtubeEmbedUrl = getYouTubeEmbedUrl(url)
  const bunnyCDNEmbedUrl = getBunnyCDNEmbedUrl(url)

  const toggleMute = () => {
    if (videoRef.current) {
      // Para vídeos HTML5
      const newMuted = !isMuted
      videoRef.current.muted = newMuted
      if (!newMuted) {
        videoRef.current.volume = volume
      }
      setIsMuted(newMuted)
    } else if (iframeRef.current) {
      // Para vídeos do YouTube
      if (isMuted) {
        // Desmutar: Primeiro garante volume, depois desmuta
        // Nota: A API do YouTube requer JSON.stringify em algumas implementações/navegadores
        iframeRef.current.contentWindow?.postMessage(JSON.stringify({
          event: 'command',
          func: 'setVolume',
          args: [volume * 100]
        }), '*')

        iframeRef.current.contentWindow?.postMessage(JSON.stringify({
          event: 'command',
          func: 'unMute'
        }), '*')
      } else {
        // Mutar
        iframeRef.current.contentWindow?.postMessage(JSON.stringify({
          event: 'command',
          func: 'mute'
        }), '*')
      }
      setIsMuted(!isMuted)
    }
  }

  // Se for YouTube, mostrar apenas indicação visual (não clicável)
  if (youtubeEmbedUrl) {
    useEffect(() => {
      const setYouTubeVolume = () => {
        if (iframeRef.current) {
          // Definir volume baseado no state (YouTube usa 0-100)
          iframeRef.current.contentWindow?.postMessage(JSON.stringify({
            event: 'command',
            func: 'setVolume',
            args: [volume * 100]
          }), '*')
        }
      }

      // Aguardar um pouco para o iframe carregar
      const timer = setTimeout(setYouTubeVolume, 2000)
      return () => clearTimeout(timer)
    }, [volume])

    return (
      <div className={`relative ${className}`}>
        <iframe
          ref={iframeRef}
          src={youtubeEmbedUrl}
          className="w-full h-full object-cover pointer-events-none"
          allow="autoplay; encrypted-media"
          allowFullScreen
          style={{ border: 'none' }}
        />
        <button
          onClick={toggleMute}
          className="absolute top-4 right-4 z-[900] bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors cursor-pointer pointer-events-auto"
          title={isMuted ? "Ativar som" : "Desativar som"}
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>
    )
  }

  // Se for BunnyCDN/Bunny Stream (iframe.mediadelivery.net)
  if (bunnyCDNEmbedUrl) {
    const bunnyPlayerRef = useRef<any>(null)
    const [isBunnyReady, setIsBunnyReady] = useState(false)

    useEffect(() => {
      // Carregar a biblioteca playerjs do Bunny dinamicamente
      const loadPlayerJS = () => {
        if ((window as any).playerjs) {
          initPlayer()
          return
        }

        const script = document.createElement('script')
        script.src = '//assets.mediadelivery.net/playerjs/playerjs-latest.min.js'
        script.async = true
        script.onload = initPlayer
        document.head.appendChild(script)
      }

      const initPlayer = () => {
        if (iframeRef.current && (window as any).playerjs) {
          bunnyPlayerRef.current = new (window as any).playerjs.Player(iframeRef.current)
          bunnyPlayerRef.current.on('ready', () => {
            setIsBunnyReady(true)
          })
        }
      }

      loadPlayerJS()
    }, [])

    const toggleBunnyMute = () => {
      if (bunnyPlayerRef.current && isBunnyReady) {
        if (isMuted) {
          bunnyPlayerRef.current.unmute()
        } else {
          bunnyPlayerRef.current.mute()
        }
        setIsMuted(!isMuted)
      }
    }

    return (
      <div className={`relative ${className}`}>
        <iframe
          ref={iframeRef}
          src={bunnyCDNEmbedUrl}
          className="w-full h-full object-cover pointer-events-none"
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
          style={{ border: 'none' }}
        />
        <button
          onClick={toggleBunnyMute}
          className="absolute top-4 right-4 z-[900] bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors cursor-pointer pointer-events-auto"
          title={isMuted ? "Ativar som" : "Desativar som"}
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>
    )
  }

  // Se for arquivo de vídeo direto (MP4, etc)
  return (
    <div className={`relative ${className}`}>
      <video
        ref={videoRef}
        autoPlay
        loop
        muted={isMuted}
        playsInline
        className="w-full h-full object-cover"
        onLoadedData={() => {
          if (videoRef.current) {
            videoRef.current.volume = volume
          }
        }}
      >
        <source src={url} type="video/mp4" />
        Seu navegador não suporta vídeos.
      </video>
      <button
        onClick={toggleMute}
        className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors cursor-pointer"
        title={isMuted ? "Ativar som" : "Desativar som"}
        style={{ pointerEvents: 'auto' }}
      >
        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
      </button>
    </div>
  )
}
